import type {
  DefaultValues,
  FieldValues,
  FormState,
  Path,
  PathValue,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import type { IAny } from '~/helpers/types';
import { storageFactory } from '~/lib/storage';
import { arePersistedValuesDifferent } from './helpers/arePersistedValuesDifferent';
import { getFilteredValues } from './helpers/getFilteredValues';
import { PersistentFormContext } from './PersistentFormContext';

export type IUsePersistentFormProps<
  TFieldValues extends FieldValues,
  TContext = IAny,
> = Omit<UseFormProps<TFieldValues, TContext>, 'defaultValues'> & {
  key?: string;
  excludeFields?: Array<Path<TFieldValues>>;
  restoreAtMount?: boolean;
  onRecoverField?: (fieldName: Path<TFieldValues>, value: unknown) => boolean;
  defaultValues?: DefaultValues<TFieldValues>;
};

export type IUsePersistentFormPackedValues<TFieldValues extends FieldValues> = {
  savedAt: number;
  values: Partial<TFieldValues>;
};

export type IUsePersistentFormResult<
  TFieldValues extends FieldValues,
  TContext = IAny,
  TTransformedValues extends FieldValues | undefined = undefined,
> = UseFormReturn<TFieldValues, TContext, TTransformedValues> & {
  recover?: () => void;
  discard?: () => void;
  canRecover?: boolean;
  recovered?: boolean;
};

const getSerializedFormState = <TFieldValues extends FieldValues>(
  formState: FormState<TFieldValues>,
): string => {
  const {
    isDirty,
    isLoading,
    isSubmitted,
    isSubmitSuccessful,
    isSubmitting,
    isValid,
    disabled,
  } = formState;

  return JSON.stringify({
    isDirty,
    isLoading,
    isSubmitted,
    isSubmitSuccessful,
    isSubmitting,
    isValid,
    disabled,
  });
};

export const usePersistentForm = <
  TFieldValues extends FieldValues,
  TContext = IAny,
  TTransformedValues extends FieldValues | undefined = undefined,
>(
  props: IUsePersistentFormProps<TFieldValues, TContext>,
): IUsePersistentFormResult<TFieldValues, TContext, TTransformedValues> => {
  const { key, excludeFields, restoreAtMount, onRecoverField, ...formProps } =
    props;

  const location = useLocation();
  const hashedKey = location.pathname;

  const context = useContext(PersistentFormContext);
  const storage = useMemo(
    () =>
      storageFactory(context.cacheLocation)({
        namespace: [context.cacheNamespace, key].filter(Boolean).join('::'),
      }),
    [context.cacheLocation, context.cacheNamespace, key],
  );

  const getPersistedValues = useCallback(():
    | Partial<TFieldValues>
    | undefined => {
    const packedValues =
      storage.get<IUsePersistentFormPackedValues<TFieldValues>>(hashedKey);
    if (!packedValues) {
      return undefined;
    }

    if (
      packedValues &&
      context.timeout !== undefined &&
      Date.now() - packedValues.savedAt > context.timeout
    ) {
      storage.delete(hashedKey);

      return undefined;
    }

    return packedValues.values;
  }, [storage, hashedKey, context.timeout]);

  const form = useForm<TFieldValues, TContext, TTransformedValues>({
    ...formProps,
    defaultValues: restoreAtMount
      ? ({
          ...formProps.defaultValues,
          ...getPersistedValues(),
        } as DefaultValues<TFieldValues>)
      : formProps.defaultValues,
  });

  const [canRecover, setCanRecover] = useState(
    !restoreAtMount &&
      arePersistedValuesDifferent(
        getPersistedValues(),
        formProps.defaultValues,
      ),
  );
  const [recovered, setRecovered] = useState(false);

  useEffect(() => {
    const subscription = form.watch(() => {
      // Workaround
      // See: https://github.com/react-hook-form/react-hook-form/issues/11734
      setTimeout(() => {
        const isDirty = form.formState.isDirty;
        if (!isDirty) {
          return;
        }

        setCanRecover(false);

        const watchedValues = form.getValues();
        const filteredWatchedValues = excludeFields?.length
          ? getFilteredValues(watchedValues, excludeFields)
          : { ...watchedValues };

        void storage.set<IUsePersistentFormPackedValues<TFieldValues>>(
          hashedKey,
          {
            savedAt: Date.now(),
            values: filteredWatchedValues,
          },
        );
      }, 0);
    });

    return () => subscription.unsubscribe();
  }, [form, storage, hashedKey, excludeFields, recovered]);

  const discard = useCallback((): void => {
    setCanRecover(false);
    storage.delete(hashedKey);
  }, [storage, hashedKey]);

  const recover = useCallback((): void => {
    setRecovered(true);
    setCanRecover(false);

    const persistedValues = getPersistedValues();
    if (!persistedValues) {
      return;
    }

    const filteredPersistedValues = excludeFields?.length
      ? getFilteredValues(persistedValues, excludeFields)
      : { ...persistedValues };

    Object.keys(filteredPersistedValues).forEach((key) => {
      const recovered = onRecoverField?.(
        key as Path<TFieldValues>,
        filteredPersistedValues[key],
      );

      if (!recovered) {
        form.setValue(
          key as Path<TFieldValues>,
          filteredPersistedValues[key] as PathValue<
            TFieldValues,
            Path<TFieldValues>
          >,
          {
            shouldValidate: false,
            shouldDirty: false,
            shouldTouch: false,
          },
        );
      }
    });
  }, [form, getPersistedValues, excludeFields, onRecoverField]);

  const formState = getSerializedFormState(form.formState);
  const value: IUsePersistentFormResult<
    TFieldValues,
    TContext,
    TTransformedValues
  > = useMemo(
    () => ({
      ...form,
      handleSubmit:
        (onSubmit) =>
        (event): Promise<void> => {
          discard();

          return form.handleSubmit(onSubmit)(event);
        },
      recover,
      discard,
      canRecover,
      recovered,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, recover, discard, canRecover, recovered, formState],
  );

  return value;
};
